import { t } from '@lingui/core/macro'
import { LucideIcon } from 'components/LucideIcon'
import {
  SidebarMenuButton,
  SidebarMenuItem,
} from 'designSystem/components/Sidebar'
import { Text } from 'designSystem/components/Text'
import type { RecordedAgenticChatSession } from 'features/embeddedSidebar/hooks/useAgenticChatSessionHistory'
import { useCallback } from 'react'

import { isAgenticChatSessionThinking } from './agenticChatHistoryRowActivity'
import { AgenticChatHistorySessionOverflowMenu } from './AgenticChatHistorySessionOverflowMenu'
import {
  HistoryItemContent,
  HistoryItemMeta,
  type HistoryItemStatus,
  HistoryItemStatusDot,
  HistoryItemTitle,
} from './agenticChatHistorySessionRowStyles'

function getStatusLabel(session: RecordedAgenticChatSession): string | null {
  if (session.syncStatus === 'archiving') return t`Archiving…`
  if (session.syncStatus === 'unarchiving') return t`Unarchiving…`
  if (session.syncStatus === 'deleting') return t`Deleting…`
  if (session.syncStatus === 'hydrating') return t`Opening…`
  if (session.persistError === 'hydrate') return t`Could not open`
  if (session.persistError === 'archive') return t`Could not archive`
  if (session.persistError === 'unarchive') return t`Could not unarchive`
  if (session.persistError === 'delete') return t`Could not delete`
  if (session.persistError === 'rename') return t`Could not rename`
  if (session.persistError === 'pin') return t`Could not pin`
  if (session.persistError === 'unpin') return t`Could not unpin`
  return null
}

function sessionRowAriaLabel({
  hasUnseenAnswer,
  isPinned,
  isThinking,
  title,
}: {
  hasUnseenAnswer: boolean
  isPinned: boolean
  isThinking: boolean
  title: string
}): string {
  if (isThinking) {
    return isPinned ? t`${title}, pinned, thinking` : t`${title}, thinking`
  }
  if (hasUnseenAnswer) {
    return isPinned ? t`${title}, pinned, new reply` : t`${title}, new reply`
  }
  return isPinned ? t`${title}, pinned` : title
}

function getHistoryItemStatus({
  isThinking,
  showUnseenAnswer,
}: {
  isThinking: boolean
  showUnseenAnswer: boolean
}): HistoryItemStatus {
  if (isThinking) return 'thinking'
  if (showUnseenAnswer) return 'unread'
  return 'idle'
}

interface AgenticChatHistorySessionRowProps {
  hasUnseenAnswer: boolean
  isActive: boolean
  isArchived?: boolean
  onArchive: (sessionKey: string, openTabId?: string) => void
  onDelete: (sessionKey: string, openTabId?: string) => void
  onPin?: (sessionKey: string, pinned: boolean) => void
  onRename: (sessionKey: string, title: string, openTabId?: string) => void
  onRetry?: (session: RecordedAgenticChatSession) => void
  onSelect: (session: RecordedAgenticChatSession, openTabId?: string) => void
  onUnarchive?: (sessionKey: string) => void
  openTabId?: string
  session: RecordedAgenticChatSession
}

export function AgenticChatHistorySessionRow({
  hasUnseenAnswer,
  isActive,
  isArchived = false,
  onArchive,
  onDelete,
  onPin,
  onRename,
  onRetry,
  onSelect,
  onUnarchive,
  openTabId,
  session,
}: AgenticChatHistorySessionRowProps) {
  const handleSelect = useCallback(() => {
    onSelect(session, openTabId)
  }, [onSelect, openTabId, session])

  const handleArchive = useCallback(() => {
    onArchive(session.key, openTabId)
  }, [onArchive, openTabId, session.key])

  const handleDelete = useCallback(() => {
    onDelete(session.key, openTabId)
  }, [onDelete, openTabId, session.key])

  const handleUnarchive = useCallback(() => {
    onUnarchive?.(session.key)
  }, [onUnarchive, session.key])

  const handleRename = useCallback(
    (title: string) => {
      onRename(session.key, title, openTabId)
    },
    [onRename, openTabId, session.key]
  )

  const handlePin = useCallback(() => {
    onPin?.(session.key, session.pinned !== true)
  }, [onPin, session.key, session.pinned])

  const handleRetry = useCallback(() => {
    onRetry?.(session)
  }, [onRetry, session])

  const statusLabel = getStatusLabel(session)
  const isThinking = isAgenticChatSessionThinking(session)
  const isPinned = session.pinned === true
  const showUnseenAnswer = hasUnseenAnswer && !isThinking && !isActive

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        aria-label={sessionRowAriaLabel({
          hasUnseenAnswer: showUnseenAnswer,
          isPinned,
          isThinking,
          title: session.title,
        })}
        isActive={isActive}
        size='sm'
        tooltip={session.title}
        type='button'
        variant='embedded'
        onClick={handleSelect}>
        <HistoryItemStatusDot
          aria-hidden='true'
          $status={getHistoryItemStatus({ isThinking, showUnseenAnswer })}
        />
        <HistoryItemContent>
          <HistoryItemTitle>
            <Text variant='caption' color='default'>
              {session.title}
            </Text>
          </HistoryItemTitle>
          {isPinned || statusLabel ? (
            <HistoryItemMeta>
              {isPinned ? <LucideIcon aria-hidden='true' name='Pin' /> : null}
              {statusLabel ? (
                <Text variant='caption' color='tertiary'>
                  {statusLabel}
                </Text>
              ) : null}
            </HistoryItemMeta>
          ) : null}
        </HistoryItemContent>
      </SidebarMenuButton>
      <AgenticChatHistorySessionOverflowMenu
        isArchived={isArchived}
        isPinned={isPinned}
        persistError={Boolean(session.persistError)}
        sessionTitle={session.title}
        onArchive={handleArchive}
        onDelete={handleDelete}
        onPin={onPin ? handlePin : undefined}
        onRename={handleRename}
        onRetry={onRetry ? handleRetry : undefined}
        onUnarchive={onUnarchive ? handleUnarchive : undefined}
      />
    </SidebarMenuItem>
  )
}
